import { subscribe } from 'valtio/vanilla'
import './style.css'
import { initHighScoreOverlay } from './components/HighScoreOverlay'
import { loadAsteroids, ASTEROIDS_VERSION } from './games/asteroids'
import { loadBreakout, BREAKOUT_VERSION } from './games/breakout'
import { loadPong, PONG_VERSION } from './games/pong'
import { loadRacer, RACER_VERSION } from './games/racer'
import { loadSnake, SNAKE_VERSION } from './games/snake'
import { submitScore, showAllHighScores } from './state/highscore'
import {
  arcadeStore,
  selectNext,
  selectPrevious,
  selectGame,
  launchGame,
  exitGame,
  getSelectedGame,
  addGame,
  updateMouseActivity,
  setMouseOverGame,
  checkMouseInactivity,
} from './state/store'
import {
  themeStore,
  ACCENT_COLORS,
  setAccentColor,
  setThemeMode,
  initTheme,
  type AccentColorKey,
} from './state/theme'

/**
 * Create a game over handler that submits the score
 */
function createGameOverHandler(gameId: string, gameVersion: string): (score: number) => void {
  return (score: number): void => {
    submitScore(gameId, score, gameVersion)
  }
}

// Register games with game over handlers
addGame({
  id: 'asteroids',
  name: 'Asteroids',
  version: ASTEROIDS_VERSION,
  load: (container) =>
    loadAsteroids(container, createGameOverHandler('asteroids', ASTEROIDS_VERSION)),
})

addGame({
  id: 'snake',
  name: 'Snake',
  version: SNAKE_VERSION,
  load: (container) => loadSnake(container, createGameOverHandler('snake', SNAKE_VERSION)),
})

addGame({
  id: 'breakout',
  name: 'Breakout',
  version: BREAKOUT_VERSION,
  load: (container) => loadBreakout(container, createGameOverHandler('breakout', BREAKOUT_VERSION)),
})

addGame({
  id: 'pong',
  name: 'Pong',
  version: PONG_VERSION,
  load: (container) => loadPong(container, createGameOverHandler('pong', PONG_VERSION)),
})

addGame({
  id: 'racer',
  name: 'Road Racer',
  version: RACER_VERSION,
  load: (container) => loadRacer(container, createGameOverHandler('racer', RACER_VERSION)),
})

/**
 * Initialize the theme picker UI and event handlers
 */
function initThemePicker(): void {
  const themeToggleBtn = document.getElementById('theme-toggle-btn')
  const themePanel = document.getElementById('theme-panel')
  const colorSwatches = document.getElementById('color-swatches')
  const modeDarkBtn = document.getElementById('mode-dark-btn')
  const modeLightBtn = document.getElementById('mode-light-btn')
  const themeIconDark = document.getElementById('theme-icon-dark')
  const themeIconLight = document.getElementById('theme-icon-light')

  if (!themeToggleBtn || !themePanel || !colorSwatches || !modeDarkBtn || !modeLightBtn) {
    console.error('Theme picker elements not found')
    return
  }

  /**
   * Render color swatches
   */
  function renderColorSwatches(): void {
    if (!colorSwatches) return

    colorSwatches.innerHTML = ''

    Object.entries(ACCENT_COLORS).forEach(([key, color]) => {
      const swatch = document.createElement('button')
      swatch.className = `color-swatch ${themeStore.accentColor === key ? 'selected' : ''}`
      swatch.style.backgroundColor = color.value
      swatch.title = color.name
      swatch.setAttribute('aria-label', `Set accent color to ${color.name}`)
      swatch.dataset.color = key

      swatch.addEventListener('click', () => {
        setAccentColor(key as AccentColorKey)
      })

      colorSwatches.appendChild(swatch)
    })
  }

  /**
   * Update mode buttons to reflect current state
   */
  function updateModeButtons(): void {
    if (!modeDarkBtn || !modeLightBtn || !themeIconDark || !themeIconLight) return

    if (themeStore.mode === 'dark') {
      modeDarkBtn.classList.add('border-accent')
      modeLightBtn.classList.remove('border-accent')
      themeIconDark.classList.remove('hidden')
      themeIconLight.classList.add('hidden')
    } else {
      modeDarkBtn.classList.remove('border-accent')
      modeLightBtn.classList.add('border-accent')
      themeIconDark.classList.add('hidden')
      themeIconLight.classList.remove('hidden')
    }
  }

  // Toggle theme panel visibility
  themeToggleBtn.addEventListener('click', (e) => {
    e.stopPropagation()
    themePanel.classList.toggle('hidden')
  })

  // Close panel when clicking outside
  document.addEventListener('click', (e) => {
    if (!themePanel.contains(e.target as Node) && e.target !== themeToggleBtn) {
      themePanel.classList.add('hidden')
    }
  })

  // Mode button handlers
  modeDarkBtn.addEventListener('click', () => setThemeMode('dark'))
  modeLightBtn.addEventListener('click', () => setThemeMode('light'))

  // Subscribe to theme changes
  subscribe(themeStore, () => {
    renderColorSwatches()
    updateModeButtons()
  })

  // Initial render
  renderColorSwatches()
  updateModeButtons()
}

/**
 * Initialize mouse tracking for cursor hiding
 */
function initMouseTracking(): void {
  const gameContainer = document.getElementById('game-container')

  if (!gameContainer) return

  // Track mouse movement over game container
  gameContainer.addEventListener('mousemove', (e) => {
    const rect = gameContainer.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    updateMouseActivity(x, y)
  })

  // Track when mouse enters/leaves game area
  gameContainer.addEventListener('mouseenter', () => {
    setMouseOverGame(true)
  })

  gameContainer.addEventListener('mouseleave', () => {
    setMouseOverGame(false)
  })

  // Check for mouse inactivity every 100ms
  setInterval(() => {
    if (arcadeStore.activeGame) {
      checkMouseInactivity()
    }
  }, 100)
}

/**
 * Update cursor visibility based on mouse state
 */
function updateCursorVisibility(): void {
  const gameContainer = document.getElementById('game-container')
  if (!gameContainer) return

  if (arcadeStore.mouse.isCursorHidden) {
    gameContainer.classList.add('cursor-hidden')
  } else {
    gameContainer.classList.remove('cursor-hidden')
  }
}

/**
 * Initialize the arcade UI and event handlers
 */
function initArcade(): void {
  const carouselTrack = document.getElementById('carousel-track')
  const gameTitle = document.getElementById('game-title')
  const gameContainer = document.getElementById('game-container')
  const exitBtn = document.getElementById('exit-btn')
  const prevBtn = document.getElementById('prev-btn')
  const nextBtn = document.getElementById('next-btn')

  if (!carouselTrack || !gameTitle || !gameContainer || !exitBtn || !prevBtn || !nextBtn) {
    console.error('Required DOM elements not found')
    return
  }

  // Initialize mouse tracking
  initMouseTracking()

  /**
   * Render the game carousel based on current state
   */
  function renderCarousel(): void {
    if (!carouselTrack || !gameTitle) return

    carouselTrack.innerHTML = ''

    arcadeStore.games.forEach((game, index) => {
      const thumbnail = document.createElement('div')
      thumbnail.className = `game-thumbnail ${index === arcadeStore.selectedIndex ? 'selected' : ''}`
      thumbnail.dataset.index = String(index)

      if (game.thumbnail) {
        const img = document.createElement('img')
        img.src = game.thumbnail
        img.alt = game.name
        thumbnail.appendChild(img)
      } else if (game.id === 'asteroids') {
        // Custom icon for Asteroids
        thumbnail.innerHTML = `
          <svg viewBox="0 0 100 100" class="w-12 h-12 md:w-16 md:h-16">
            <polygon points="50,10 90,90 10,90" fill="none" stroke="currentColor" stroke-width="3" class="text-theme-secondary"/>
            <circle cx="30" cy="50" r="8" fill="none" stroke="currentColor" stroke-width="2" class="text-theme-secondary"/>
            <circle cx="70" cy="40" r="12" fill="none" stroke="currentColor" stroke-width="2" class="text-theme-secondary"/>
          </svg>
        `
      } else if (game.id === 'snake') {
        // Custom icon for Snake - coiled snake with apple (monochrome)
        thumbnail.innerHTML = `
          <svg viewBox="0 0 100 100" class="w-12 h-12 md:w-16 md:h-16">
            <!-- Snake body - S-curve shape -->
            <path d="M75,25 Q90,25 90,40 Q90,55 75,55 L35,55 Q20,55 20,70 Q20,85 35,85 L50,85" 
                  fill="none" stroke="currentColor" stroke-width="8" stroke-linecap="round" stroke-linejoin="round" class="text-theme-secondary"/>
            <!-- Snake head -->
            <circle cx="75" cy="25" r="10" fill="none" stroke="currentColor" stroke-width="3" class="text-theme-secondary"/>
            <!-- Snake eyes -->
            <circle cx="78" cy="23" r="2" fill="currentColor" class="text-theme-secondary"/>
            <circle cx="72" cy="23" r="2" fill="currentColor" class="text-theme-secondary"/>
            <!-- Snake tongue -->
            <path d="M82,28 L88,25 M82,28 L88,31" stroke="currentColor" stroke-width="2" stroke-linecap="round" class="text-theme-secondary"/>
            <!-- Apple/food -->
            <circle cx="50" cy="85" r="7" fill="none" stroke="currentColor" stroke-width="2" class="text-theme-secondary"/>
            <path d="M50,78 Q53,75 56,77" stroke="currentColor" stroke-width="2" fill="none" class="text-theme-secondary"/>
          </svg>
        `
      } else if (game.id === 'pong') {
        // Custom icon for Pong - two paddles and ball (monochrome)
        thumbnail.innerHTML = `
          <svg viewBox="0 0 100 100" class="w-12 h-12 md:w-16 md:h-16">
            <!-- Left paddle -->
            <rect x="15" y="30" width="8" height="40" rx="2" fill="currentColor" class="text-theme-secondary"/>
            <!-- Right paddle -->
            <rect x="77" y="30" width="8" height="40" rx="2" fill="currentColor" class="text-theme-secondary"/>
            <!-- Ball -->
            <circle cx="50" cy="50" r="8" fill="currentColor" class="text-theme-secondary"/>
            <!-- Center line (dashed) -->
            <line x1="50" y1="10" x2="50" y2="20" stroke="currentColor" stroke-width="2" class="text-theme-secondary"/>
            <line x1="50" y1="30" x2="50" y2="40" stroke="currentColor" stroke-width="2" class="text-theme-secondary"/>
            <line x1="50" y1="60" x2="50" y2="70" stroke="currentColor" stroke-width="2" class="text-theme-secondary"/>
            <line x1="50" y1="80" x2="50" y2="90" stroke="currentColor" stroke-width="2" class="text-theme-secondary"/>
          </svg>
        `
      } else if (game.id === 'racer') {
        // Custom icon for Road Racer - car on road with lane lines (monochrome)
        thumbnail.innerHTML = `
          <svg viewBox="0 0 100 100" class="w-12 h-12 md:w-16 md:h-16">
            <!-- Road edges -->
            <line x1="25" y1="10" x2="25" y2="90" stroke="currentColor" stroke-width="3" class="text-theme-secondary"/>
            <line x1="75" y1="10" x2="75" y2="90" stroke="currentColor" stroke-width="3" class="text-theme-secondary"/>
            <!-- Lane dividers (dashed) -->
            <line x1="50" y1="10" x2="50" y2="25" stroke="currentColor" stroke-width="2" class="text-theme-secondary"/>
            <line x1="50" y1="35" x2="50" y2="50" stroke="currentColor" stroke-width="2" class="text-theme-secondary"/>
            <line x1="50" y1="60" x2="50" y2="75" stroke="currentColor" stroke-width="2" class="text-theme-secondary"/>
            <!-- Car body -->
            <rect x="35" y="55" width="18" height="30" rx="4" fill="currentColor" class="text-theme-secondary"/>
            <!-- Car windshield -->
            <rect x="38" y="60" width="12" height="8" rx="1" fill="none" stroke="currentColor" stroke-width="1" class="text-theme-secondary" opacity="0.5"/>
            <!-- Wheels -->
            <rect x="32" y="58" width="4" height="8" rx="1" fill="currentColor" class="text-theme-secondary"/>
            <rect x="52" y="58" width="4" height="8" rx="1" fill="currentColor" class="text-theme-secondary"/>
            <rect x="32" y="74" width="4" height="8" rx="1" fill="currentColor" class="text-theme-secondary"/>
            <rect x="52" y="74" width="4" height="8" rx="1" fill="currentColor" class="text-theme-secondary"/>
            <!-- Obstacle car (smaller, at top) -->
            <rect x="55" y="15" width="14" height="22" rx="3" fill="none" stroke="currentColor" stroke-width="2" class="text-theme-secondary"/>
          </svg>
        `
      } else if (game.id === 'breakout') {
        // Custom icon for Breakout - paddle, ball, and bricks (monochrome)
        thumbnail.innerHTML = `
          <svg viewBox="0 0 100 100" class="w-12 h-12 md:w-16 md:h-16">
            <!-- Bricks at top -->
            <rect x="10" y="10" width="20" height="8" fill="currentColor" class="text-theme-secondary"/>
            <rect x="35" y="10" width="20" height="8" fill="currentColor" class="text-theme-secondary"/>
            <rect x="60" y="10" width="20" height="8" fill="currentColor" class="text-theme-secondary"/>
            <rect x="10" y="23" width="20" height="8" fill="currentColor" class="text-theme-secondary"/>
            <rect x="35" y="23" width="20" height="8" fill="currentColor" class="text-theme-secondary"/>
            <rect x="60" y="23" width="20" height="8" fill="currentColor" class="text-theme-secondary"/>
            <!-- Ball -->
            <circle cx="50" cy="60" r="6" fill="currentColor" class="text-theme-secondary"/>
            <!-- Paddle at bottom -->
            <rect x="35" y="80" width="30" height="8" rx="2" fill="currentColor" class="text-theme-secondary"/>
          </svg>
        `
      } else {
        // Placeholder icon for games without thumbnails
        thumbnail.innerHTML = `
          <span class="text-2xl text-theme-secondary">?</span>
        `
      }

      thumbnail.addEventListener('click', () => {
        selectGame(index)
        if (gameContainer) {
          launchGame(gameContainer)
        }
      })

      carouselTrack.appendChild(thumbnail)
    })

    // Update game title
    const selectedGame = getSelectedGame()
    gameTitle.textContent = selectedGame?.name ?? 'Select a game'
  }

  /**
   * Render the game screen based on current state
   */
  function renderGameScreen(): void {
    if (!gameContainer || !exitBtn) return

    if (arcadeStore.activeGame) {
      exitBtn.classList.remove('hidden')
      // Game content is rendered by the game's load function
    } else {
      exitBtn.classList.add('hidden')
      gameContainer.innerHTML = `
        <p class="text-accent text-xs md:text-sm text-center px-4">
          Press ENTER or click a game to play
        </p>
      `
    }
  }

  // Subscribe to store changes
  subscribe(arcadeStore, () => {
    renderCarousel()
    renderGameScreen()
    updateCursorVisibility()
  })

  // Initial render
  renderCarousel()
  renderGameScreen()

  // Button event handlers
  prevBtn.addEventListener('click', selectPrevious)
  nextBtn.addEventListener('click', selectNext)
  exitBtn.addEventListener('click', exitGame)

  // Keyboard navigation
  document.addEventListener('keydown', (event) => {
    // Don't handle keys if a game is active (let the game handle them)
    if (arcadeStore.activeGame && event.key !== 'Escape') {
      return
    }

    switch (event.key) {
      case 'ArrowLeft':
        selectPrevious()
        event.preventDefault()
        break
      case 'ArrowRight':
        selectNext()
        event.preventDefault()
        break
      case 'Enter':
        launchGame(gameContainer)
        event.preventDefault()
        break
      case 'Escape':
        if (arcadeStore.activeGame) {
          exitGame()
          event.preventDefault()
        }
        break
    }
  })
}

/**
 * Initialize high scores button
 */
function initHighScoresButton(): void {
  const highscoresBtn = document.getElementById('highscores-btn')

  if (!highscoresBtn) {
    console.error('High scores button not found')
    return
  }

  highscoresBtn.addEventListener('click', () => {
    showAllHighScores()
  })
}

/**
 * Initialize the application
 */
function init(): void {
  // Initialize theme first
  initTheme()

  // Initialize theme picker
  initThemePicker()

  // Initialize high score overlay
  initHighScoreOverlay()

  // Initialize high scores button
  initHighScoresButton()

  // Initialize arcade
  initArcade()
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init)
} else {
  init()
}
